import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import { toast } from "sonner";
import { X, Building2, Link2, Info, Loader2 } from "lucide-react";

/**
 * Self-service Join Company modal.
 * Validates the access code against `/api/auth/link-company`,
 * then refreshes the auth session so the user shows up under the company.
 *
 * Props:
 *   onClose(): close the modal
 *   onJoined(company): notify parent of success
 */
export default function JoinCompanyModal({ onClose, onJoined }) {
  useBodyScrollLock(true);
  const { refresh } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const submit = async (e) => {
    e.preventDefault();
    const clean = code.trim().toUpperCase();
    if (!clean) { setError("Enter your company's access code"); return; }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/link-company", { code: clean });
      const companyName = data?.company_name || data?.company?.name || "company";
      toast.success(`Joined ${companyName}`);
      await refresh();
      onJoined?.(data);
      onClose();
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Invalid or expired access code";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const body = (
    <>
      <div
        data-testid="join-company-backdrop"
        className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm animate-in fade-in duration-150"
        onClick={() => !busy && onClose()}
      />
      <div
        role="dialog"
        aria-modal="true"
        data-testid="join-company-modal"
        className="fixed inset-0 z-[61] flex items-end md:items-center justify-center p-0 md:p-6 pointer-events-none"
      >
        <div
          className="w-full md:max-w-md max-h-[92vh] overflow-y-auto border-2 shadow-2xl pointer-events-auto animate-in slide-in-from-bottom-4 md:zoom-in-95 duration-200"
          style={{ backgroundColor: "#0B1E3F", borderColor: "#F9D71C", color: "#fff" }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-5 py-4 border-b-2 sticky top-0"
            style={{ backgroundColor: "#0B1E3F", borderBottomColor: "rgba(249,215,28,0.4)" }}
          >
            <div className="inline-flex items-center gap-3 min-w-0">
              <span className="inline-flex items-center justify-center h-10 w-10 bg-safety-yellow text-black border-2 border-safety-yellow shrink-0">
                <Building2 className="w-5 h-5" strokeWidth={3} />
              </span>
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: "#F9D71C" }}>
                  Self-Service
                </div>
                <div className="font-heading text-xl truncate">Join Company</div>
              </div>
            </div>
            <button
              data-testid="join-company-close"
              onClick={() => !busy && onClose()}
              className="h-10 w-10 inline-flex items-center justify-center border-2 hover:bg-safety-yellow hover:text-black hover:border-safety-yellow transition-colors"
              style={{ borderColor: "#F9D71C" }}
              aria-label="Close"
            >
              <X className="w-5 h-5" strokeWidth={3} />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={submit} className="px-5 py-5 space-y-5">
            {/* Instructional callout */}
            <div
              className="flex gap-3 p-3 border-2"
              style={{ borderColor: "rgba(249,215,28,0.4)", backgroundColor: "rgba(249,215,28,0.08)" }}
            >
              <Info className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#F9D71C" }} strokeWidth={3} />
              <p className="text-xs leading-relaxed text-white/85">
                <b className="text-white">Need a code?</b> Contact a Company Admin or Supervisor to
                request your company&apos;s unique Access Code.
              </p>
            </div>

            <div>
              <label htmlFor="join-code-input" className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/60 mb-2 block">
                Company Access Code
              </label>
              <input
                id="join-code-input"
                ref={inputRef}
                data-testid="join-code-input"
                value={code}
                onChange={(e) => { setCode(e.target.value); if (error) setError(""); }}
                placeholder="e.g. KARM2026"
                autoCapitalize="characters"
                autoComplete="off"
                className="w-full h-14 px-4 bg-white/5 border-2 text-white font-mono text-xl tracking-[0.25em] uppercase placeholder:text-white/30 placeholder:tracking-normal placeholder:font-sans placeholder:text-base"
                style={{ borderColor: error ? "#FF3B30" : "#F9D71C" }}
                maxLength={32}
              />
              {error && (
                <p data-testid="join-code-error" className="mt-2 text-xs font-bold uppercase tracking-wider text-status-expired">
                  {error}
                </p>
              )}
            </div>

            <button
              type="submit"
              data-testid="verify-and-join"
              disabled={busy || !code.trim()}
              className="w-full h-12 inline-flex items-center justify-center gap-2 bg-safety-yellow text-black border-2 border-safety-yellow font-black uppercase tracking-wider hover:bg-safety-yellowHover transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} />
                  Verifying…
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4" strokeWidth={3} />
                  Verify &amp; Join
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </>
  );

  return createPortal(body, document.body);
}
